import { Select } from '@servicecompressionllc/sc-design-system';
import { Toggle } from '../../../components/Toggle.js';
import { TemplateInput } from '../controls/TemplateInput.js';
import type { StepEditorProps } from './types.js';
import { useReportStepOptions } from './stepOptions.js';

/** Periods named relative to each run, so a schedule always covers fresh data. */
export const REPORT_PERIODS = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today so far' },
  { value: 'last 24 hours', label: 'Last 24 hours' },
  { value: 'last 7 days', label: 'Last 7 days' },
  { value: 'this month', label: 'This month so far' },
  { value: 'last month', label: 'Last month' },
];

/** The picker's "no narrowing" choice; a Select has no way to clear itself. */
const ANY = '__any__';

/** A customer or region: a picker, or a value from the event when the trigger carries one. */
const NarrowField = ({
  label,
  value,
  options,
  error,
  vars,
  source,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  error: string | null | undefined;
  vars: StepEditorProps<'report'>['vars'];
  source: string | null;
  onChange: (v: string) => void;
}) => {
  if (source)
    return (
      <TemplateInput label={label} value={value} vars={vars} source={source} onChange={onChange} />
    );
  // A name saved earlier stays pickable even if the list no longer has it.
  const known = !value || options.some((o) => o.value === value);
  return (
    <label className="grid min-w-0 gap-1 text-xs text-ink-muted">
      {label}
      <Select
        aria-label={label}
        options={[
          { value: ANY, label: `Any ${label.toLowerCase()}` },
          ...options,
          ...(known ? [] : [{ value, label: value }]),
        ]}
        value={[value || ANY]}
        placeholder={error ?? `Any ${label.toLowerCase()}`}
        emptyMessage="None match."
        onChange={(picked) => onChange(picked[0] === ANY ? '' : (picked[0] ?? ''))}
      />
      {error && <span className="text-danger">{error}</span>}
    </label>
  );
};

export const ReportStepEditor = ({ step, ctx, vars, onChange: put }: StepEditorProps<'report'>) => {
  const options = useReportStepOptions();
  const reports = options.reportOptions;
  const picked = reports.find((r) => r.value === step.templateId);
  // Switching reports keeps only the values the new one takes.
  const pick = (templateId: string) => {
    const next = reports.find((r) => r.value === templateId);
    put({
      ...step,
      templateId,
      ...(next?.kind === 'unit_list' ? { customer: '', region: '' } : { units: '' }),
    });
  };
  // A period saved some other way (a fixed month) stays pickable rather than vanishing.
  const periods = REPORT_PERIODS.some((p) => p.value === step.period)
    ? REPORT_PERIODS
    : [...REPORT_PERIODS, ...(step.period ? [{ value: step.period, label: step.period }] : [])];
  return (
    <>
      <label className="grid min-w-0 gap-1 text-xs text-ink-muted">
        Report
        <Select
          aria-label="Report"
          options={reports}
          value={step.templateId ? [step.templateId] : []}
          placeholder={options.reportsError ? 'Reports are unavailable' : 'Pick a report'}
          emptyMessage="No reports match."
          onChange={(ids) => pick(ids[0] ?? '')}
        />
      </label>
      {options.reportsError && <p className="text-xs text-danger">{options.reportsError}</p>}
      {picked && <p className="-mt-1 text-xs text-ink-subtle">{picked.covers}</p>}
      {picked?.kind === 'unit_list' ? (
        <TemplateInput
          label="Units"
          value={step.units}
          vars={vars}
          source={ctx.eventSource}
          onChange={(v) => put({ ...step, units: v })}
        />
      ) : (
        picked && (
          <>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              <NarrowField
                label="Customer"
                value={step.customer}
                options={options.customerOptions}
                error={options.customersError}
                vars={vars}
                source={ctx.eventSource}
                onChange={(v) => put({ ...step, customer: v })}
              />
              <NarrowField
                label="Region"
                value={step.region}
                options={options.regionOptions}
                error={options.regionsError}
                vars={vars}
                source={ctx.eventSource}
                onChange={(v) => put({ ...step, region: v })}
              />
            </div>
            <p className="-mt-1 text-xs text-ink-subtle">
              Optional. Narrow each run to a customer, a region or both.
            </p>
          </>
        )
      )}
      <label className="grid min-w-0 max-w-[50%] gap-1 text-xs text-ink-muted">
        Period
        <Select
          aria-label="Period"
          options={periods}
          value={step.period ? [step.period] : []}
          searchable={false}
          placeholder="Pick a period"
          onChange={(picked) => put({ ...step, period: picked[0] ?? '' })}
        />
      </label>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-ink">Send the download link when it&rsquo;s ready</span>
        <Toggle
          checked={step.sendLink}
          label="Send the download link when it's ready"
          onChange={() => put({ ...step, sendLink: !step.sendLink })}
        />
      </div>
      {step.sendLink && (
        <label className="grid min-w-0 gap-1 text-xs text-ink-muted">
          Who gets the link?
          <Select
            aria-label="Who gets the link?"
            multiple
            options={ctx.peopleOptions ?? []}
            value={step.recipients.map((r) => r.id)}
            placeholder="Pick people"
            onChange={(ids) =>
              put({ ...step, recipients: ids.map((id) => ({ kind: 'user', id })) })
            }
          />
        </label>
      )}
      <p className="text-xs text-ink-subtle">
        The report runs as the person who created this workflow, with their access. The link works
        for 24 hours.{' '}
        {step.sendLink
          ? 'If it fails, only the creator is told.'
          : 'Nobody is messaged; the PDF waits in the creator’s runs under Reports.'}
      </p>
    </>
  );
};
